import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import MaskedInput from 'react-text-mask';
import PropTypes from 'prop-types';

import UserContext from '@/contexts/UserContext';
import { masksByCountry } from '@/helpers/mask';

const inputBaseClass =
  "w-full rounded-[4px] border border-[#ced4da] bg-white !h-[37px] !px-[12px] py-[6px] font-['Open_Sans'] text-[14px] font-normal outline-none transition-all placeholder:text-gray-400 focus:border-[#80bdff] disabled:bg-[#efefef]";

class RequestFormPeople extends Component {
  static contextType = UserContext;

  constructor(props, context) {
    super(props, context);
    this.state = {
      name: props.formData?.name || '',
      document: props.formData?.document || '',
      signature: props.formData?.signature !== undefined ? !!props.formData.signature : true,
      title: props.formData?.title,
    };
  }

  componentDidUpdate(prevProps) {
    const { formData } = this.props;

    if (
      formData?.name !== prevProps.formData?.name ||
      formData?.document !== prevProps.formData?.document ||
      formData?.signature !== prevProps.formData?.signature ||
      formData?.title !== prevProps.formData?.title
    ) {
      this.setState({
        name: formData?.name || '',
        document: formData?.document || '',
        signature: formData?.signature !== undefined ? !!formData.signature : true,
        title: formData?.title,
      });
    }
  }

  onChange = (name, title, preserveCustomTitle = false) => (event) => {
    const { onChange } = this.props;
    this.setState(
      {
        [name]: event.target.value,
        ...(preserveCustomTitle ? {} : { title }),
      },
      () => onChange(this.state)
    );
  };

  onChangeTitle = (event) => {
    const { onChange } = this.props;
    const newTitle = event.target.value;
    this.setState({ title: newTitle }, () => onChange(this.state));
  };

  onChangeSig = (name) => (event) => {
    const { onChange } = this.props;
    this.setState(
      {
        [name]: event.target.checked,
      },
      () => onChange(this.state)
    );
  };

  render() {
    const { disabled, idSchema, intl, schema, uiSchema } = this.props;
    const { name, document, signature, title } = this.state;
    const me = this.context;
    const displayTitle = schema?.title || uiSchema?.['ui:title'] || '';
    const idParts = idSchema?.$id ? idSchema.$id.split('_') : [];
    const cleanParts = idParts.filter(part => !/^\d+$/.test(part));
    const fieldName = cleanParts[cleanParts.length - 1];

    const translatedTitle = fieldName
      ? intl.formatMessage({ id: `PERSON.${fieldName.toUpperCase()}`, defaultMessage: displayTitle })
      : displayTitle;

    const isOther = fieldName === 'other';
    const canCustomizeTitle = isOther && (
      schema?.allowCustomTitle === true ||
      uiSchema?.['ui:options']?.allowCustomTitle === true
    );
    const defaultTitle = translatedTitle || intl.formatMessage({ id: 'PERSON.OTHER', defaultMessage: 'Outro' });
    const displayedTitle = canCustomizeTitle && title?.trim() ? title : defaultTitle;
    const dataCyBase = String(displayedTitle || idSchema?.$id || 'people').toLowerCase();
    const isArrayItem = /_\d+$/.test(idSchema?.$id || '');
    const shouldRenderOwnLabel = !!translatedTitle && !isArrayItem;

    const country = me?.profile?.country || 'BR';
    const isBrazilian = country === 'BR';
    const cnpjMask = masksByCountry[country].CNPJ_MASK;
    const cpfMask = masksByCountry[country].CPF_MASK;

    return (
      <div className="mb-3">
        {shouldRenderOwnLabel && (
          <label
            htmlFor={idSchema.$id}
            className="mb-[0.5rem] mt-[0.3vh] inline-block font-['Open_Sans'] text-[12px] font-bold text-[#212529]"
          >
            {displayedTitle}
          </label>
        )}

        <div className="row g-2 items-center">
          {canCustomizeTitle && (
            <div className="col-12 mb-2">
              <input
                disabled={disabled}
                value={title || ''}
                onChange={this.onChangeTitle}
                placeholder={intl.formatMessage({
                  id: 'party_title_placeholder',
                  defaultMessage: 'Título (ex: Locatário Solidário, Inventariante)',
                })}
                data-cy={`${dataCyBase}-title`}
                className={inputBaseClass}
              />
            </div>
          )}

          <div className="col-12">
            <input
              disabled={disabled}
              id={idSchema.$id}
              value={name || ''}
              onChange={this.onChange('name', defaultTitle, canCustomizeTitle)}
              placeholder={intl.formatMessage({ id: 'name' })}
              data-cy={`${dataCyBase}-name`}
              style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
              className={inputBaseClass}
            />
          </div>

          <div className="col-6">
            <MaskedInput
              disabled={disabled}
              guide={false}
              value={document || ''}
              onChange={this.onChange('document', defaultTitle, canCustomizeTitle)}
              placeholder={intl.formatMessage({ id: 'document' })}
              data-cy={`${dataCyBase}-document`}
              mask={(value) => {
                if (isBrazilian && /[A-Za-z]/.test(value)) {
                  return cnpjMask;
                }
                const numbersOnly = value.replace(/\D/g, '');
                return numbersOnly.length > 11 ? cnpjMask : cpfMask;
              }}
              style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
              className={inputBaseClass}
            />
          </div>

          <div className="col-4 flex items-center gap-2">
            <input
              type="checkbox"
              id={`${idSchema.$id}-signature`}
              disabled={disabled}
              onChange={this.onChangeSig('signature')}
              checked={!!signature}
              data-cy={`${dataCyBase}-signature`}
              className="w-4 h-4 text-[#4055b3] border-[#ced4da] rounded focus:ring-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <label 
              htmlFor={`${idSchema.$id}-signature`}
              className="text-[12px] text-[#212529] font-bold font-['Open_Sans'] cursor-pointer leading-none"
            >
              <FormattedMessage id="signature" />
            </label>
          </div>
        </div>
      </div>
    );
  }
}

RequestFormPeople.propTypes = {
  disabled: PropTypes.bool,
  formData: PropTypes.object.isRequired,
  idSchema: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object,
};

RequestFormPeople.defaultProps = {
  disabled: false,
  uiSchema: {},
};

export default injectIntl(RequestFormPeople);
